import { getMomentTz } from "@dexteel/mesf-core";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import {
	endOfMonth,
	startOfMonth,
	suggestFreeWindow,
	toDateKey,
} from "@/pages/calendar/utils/calendarUtils";
import { getCalendarEvents } from "@/shared/repositories/CalendarEventRepository";

const moment = getMomentTz();

/**
 * A work order created here still needs a plan window — an unplanned one has no
 * position in a queue ordered by plan. The editor opens on the first free
 * stretch of the shift being viewed, so the common case is one click.
 *
 * Fetched only while the create modal is opening, and over the whole month so
 * the editor can flag collisions on any date the user picks. Same range and
 * cache key as the calendar page, so arriving from there costs nothing.
 */
export const useNewWorkOrderWindow = ({
	assetId,
	shiftStart,
	shiftEnd,
	anchorDate = null,
	enabled,
}: {
	assetId: number | null;
	shiftStart: Date | null;
	shiftEnd: Date | null;
	/** row being edited — its month is fetched instead of the viewed shift's */
	anchorDate?: Date | string | null;
	enabled: boolean;
}) => {
	// Editing anchors on the row's own production date so its event is always in
	// range; creating anchors on the shift being viewed
	const anchor = anchorDate ? new Date(anchorDate) : shiftStart;
	const rangeStart = anchor ? toDateKey(startOfMonth(anchor)) : null;
	const rangeEnd = anchor ? toDateKey(endOfMonth(anchor)) : null;

	const { data, isFetching } = useQuery({
		queryKey: ["calendarEvents", assetId, rangeStart, rangeEnd],
		queryFn: ({ signal }) =>
			getCalendarEvents(
				{
					start: rangeStart as string,
					end: rangeEnd as string,
					assetId: assetId as number,
				},
				signal,
			),
		enabled: enabled && !!assetId && !!rangeStart,
		staleTime: 30_000,
		meta: { realtime: ["FAB.GetCalendarEvents"] },
	});

	const window = useMemo(() => {
		if (!shiftStart || !shiftEnd) return null;
		const from = moment.utc(shiftStart).valueOf();
		const to = moment.utc(shiftEnd).valueOf();
		return suggestFreeWindow({
			preferredStart: from,
			preferredMs: to - from,
			lowerBound: from,
			busy: (data?.events ?? []).map((ev) => ({
				s: moment.utc(ev.Start).valueOf(),
				e: moment.utc(ev.End).valueOf(),
			})),
		});
	}, [data, shiftStart, shiftEnd]);

	return {
		window,
		lineEvents: data?.events ?? [],
		isLoading: isFetching,
	};
};
